import { button, countOf, line, readingWindow, saidUnder, type ReadingWindow } from "./shared";

/*
 * The Book of Eras: the world's own history, one page per age (chunk U-0's
 * split of `panels.ts`; chunk U-2's move into the reading window).
 *
 * One panel drawn in two places — the session opening on it, and the copy of it
 * standing on a town's shelf. The anatomy is identical and only the box moves,
 * which is the exit criterion made structural: the same text, read two ways.
 *
 * This is the surface the owner pointed at when the reading window was ruled
 * (2026-09-01), so it is the one page here that did not have to move: what
 * changed is that the frame it already wore is now the kit's, and the market
 * board and a chronicle wear it too.
 */

/** One age, as the Book of Eras draws it. See {@link ErasPage}. */
export interface ErasPage {
  /** The age's name, as the simulation spells it. Never composed here. */
  readonly heading: string;

  /**
   * The other naming surface, under the heading, or `""` once the taste call is
   * settled (`witness/saga.ts`'s `ERA_HEADING`).
   */
  readonly under: string;

  /** The age's lines, in reading order. Every one of them cites a record (O6). */
  readonly lines: readonly string[];
}

/** Everything the Book of Eras draws. See {@link ErasPanel.show}. */
export interface ErasView {
  /**
   * Which of the two readings this is: the session opening on it, or a copy
   * standing on a shelf.
   *
   * The only thing it changes is the box — where the page sits and what it
   * offers to press. The content is identical by construction, which is the
   * point: the O6 exit criterion is that *the same text* is readable in-world,
   * so it is the same panel, filled from the same projection.
   */
  readonly where: "opening" | "shelf";

  /** The book's own title line. Chrome about the object, like a chronicle's. */
  readonly title: string;

  /** One page per age, oldest first. */
  readonly pages: readonly ErasPage[];

  /** Which page is open. Clamped here, so a caller may count past the end. */
  readonly page: number;

  /** The foot line: how to turn a page, and how to leave. */
  readonly hint: string;

  /** Turns to a page. `null` at a shelf, where the interact key turns them. */
  readonly onTurn?: ((page: number) => void) | null;

  /** Closes the book and lets the session begin. Only the opening has one. */
  readonly onBegin?: (() => void) | null;
}

/**
 * The Book of Eras: the world's own history, one page per age.
 *
 * Phase 6 decision O6's read side, and the phase's opening image — *"the game
 * opens with the player reading the world's actual history in a book that exists
 * on shelves"*. Every line on it is `sim-core`'s, composed in the Saga register
 * over records the log holds, and this file puts them on a page and composes
 * nothing (`witness/saga.ts` is the seam, and the only module that reads it).
 *
 * **One panel, two readings, and that is the exit criterion made structural.**
 * The session opens on it and a copy of it stands on a town's shelf; if those
 * were two panels then "the same text is readable in-world" would be a claim
 * about two code paths instead of a fact about one. What the `where` of a
 * {@link ErasView} changes is the box and the way out — centred with buttons
 * when it is the first thing a session does, in the book's own corner with the
 * pointer still locked when it is read off a shelf.
 *
 * **It paginates by age rather than scrolling.** Six ages of eight lines is a
 * wall of text in one column and a book in six pages, and the ages are the
 * simulation's own division of the record (decision 2's era rule) rather than a
 * length the client chose to cut at.
 *
 * There are no record tags on the page, for {@link BookPanel}'s reason: a book
 * is the world talking, and the ids are one keypress away in the trace view —
 * where, since this phase, every line of this book is listed with the records it
 * cites.
 */
export class ErasPanel {
  /**
   * What the opening reading is read *against*: everything else, dimmed.
   *
   * Its own element rather than a background on the panel, because it is also
   * the way out — a click anywhere off the page closes the book and starts the
   * session, which is the plainest possible reading of "skippable at any point".
   * Hidden entirely at a shelf, where the world behind the book is the thing the
   * player walked up to it through.
   *
   * It survived chunk U-2 for exactly that reason. The reading window paints its
   * own veil for the two mid-session readings, but this one has a *click* on it
   * and a crosshair suppression asking about it by name (`ui/reading.css`), so
   * the opening's frame stays the element it always was and the centre over it
   * takes no pointer events at all.
   */
  private readonly backdrop: HTMLElement;

  private readonly frame: ReadingWindow;
  private readonly countElement: HTMLElement;
  private readonly buttonsElement: HTMLElement;

  /** Which page is on screen, so a caller that only wants to turn one can ask. */
  private shown = 0;

  /** How many pages the open book has, so a turn can be clamped without one. */
  private pages = 0;

  constructor(root: HTMLElement) {
    this.backdrop = document.createElement("div");
    this.backdrop.className = "hud__eras-back";
    this.backdrop.hidden = true;

    // The head's three lines, and which `hud__*` name each one keeps: the book's
    // own label goes on the eyebrow — it is chrome about the object, *"the book
    // of eras — kept in Jorgarth"* — and the two lines that name the AGE are the
    // title and the under-line, which is what the page has always said and what
    // the lab drew.
    this.frame = readingWindow({
      surface: "eras",
      eyebrow: "hud__panel-title",
      title: "hud__eras-heading",
      under: "hud__eras-under",
    });

    this.countElement = document.createElement("div");
    this.countElement.className = "hud__panel-count";

    this.buttonsElement = document.createElement("div");
    this.buttonsElement.className = "hud__eras-buttons push";

    // The foot stays inside the frame and the hint does not: which age of how
    // many and the two page turners are facts about the *book*, and a keyboard
    // is a fact about the player (the lab's rule, and {@link ReadingWindow.hint}
    // carries the argument). The hint used to be the third thing in this row.
    const foot = document.createElement("div");
    foot.className = "hud__eras-foot panel-footer";
    foot.append(this.countElement, this.buttonsElement);
    this.frame.page.append(foot);

    // The backdrop first, so the page is drawn over it — the same document-order
    // rule the panels are attached under (`hud/hud.ts`).
    root.append(this.backdrop, this.frame.element);
  }

  get open(): boolean {
    return !this.frame.element.hidden;
  }

  /** Which page is open. `0` when the book is shut. */
  get page(): number {
    return this.shown;
  }

  /** How many pages the open book has. `0` when it is shut. */
  get count(): number {
    return this.pages;
  }

  /**
   * Puts one age on the page and shows the book.
   *
   * Called again for every turn rather than mutating a page in place, which is
   * the habit of every other panel here: a view goes in, the DOM is rebuilt, and
   * nothing in this file remembers anything about the world between calls.
   */
  show(view: ErasView): void {
    this.pages = view.pages.length;
    this.shown = Math.min(Math.max(view.page, 0), Math.max(view.pages.length - 1, 0));

    const page = view.pages[this.shown];

    this.frame.element.className =
      view.where === "opening"
        ? "hud__panel--eras hud__reading reading-centre hud__panel--eras-opening"
        : "hud__panel--eras hud__reading reading-centre";
    this.backdrop.hidden = view.where !== "opening";

    if (this.frame.eyebrow !== null) {
      this.frame.eyebrow.textContent = view.title;
    }

    this.frame.title.textContent = page?.heading ?? "";
    saidUnder(this.frame.under, page?.under ?? "");

    this.frame.body.replaceChildren(
      ...(page === undefined || page.lines.length === 0
        ? [line("this world has no age worth an age's name yet")]
        : page.lines.map((text) => {
            const written = document.createElement("div");
            written.className = "hud__eras-line reading-line";
            written.textContent = text;

            return written;
          })),
    );

    // "3 of 6 ages" — chrome about the object, and the one number on the page: a
    // reader three pages in should be able to see how much book is left without
    // counting the ages themselves. Numerals rather than words on purpose: the
    // simulation already has a vocabulary for an age's number ("the third age",
    // which is what {@link ErasPage.under} may be carrying), and a client that
    // spelled its own would be a second one to disagree with it.
    this.countElement.textContent =
      view.pages.length === 0
        ? ""
        : `${this.shown + 1} of ${countOf(view.pages.length, "age")}`;

    this.buttonsElement.replaceChildren(...this.controls(view));
    this.frame.hint.textContent = view.hint;
    this.frame.element.hidden = false;
    // Every turn starts at the top of the page. A book is turned, not scrolled,
    // and a page that opened halfway down would be the previous age's scroll
    // position kept for an age it has nothing to do with.
    this.frame.body.scrollTop = 0;

    // The backdrop's click is the way out, rebound with the view because the
    // callback belongs to whoever opened the book.
    this.backdrop.onclick =
      view.onBegin === undefined || view.onBegin === null
        ? null
        : (event: MouseEvent): void => {
            event.stopPropagation();
            view.onBegin?.();
          };
  }

  hide(): void {
    this.frame.element.hidden = true;
    this.backdrop.hidden = true;
    this.backdrop.onclick = null;
    this.pages = 0;
    this.shown = 0;
  }

  /**
   * The buttons under the page: back, on, and the way out.
   *
   * Only where there is a mouse to press them. At a shelf the pointer is locked
   * and the same `F` that opened the book turns its pages
   * (`witness/witness.ts`), so the foot carries the count and nothing to click.
   *
   * **The kit's `.panel-button` composed onto the client's own name**, which is
   * decision D2 at the smallest scale there is: `hud__panel-button` stays,
   * because it is what the origins screen's foot and three check files select
   * by, and the kit class rides beside it for the shape a page turner wants at
   * this size. The gold edge goes on the *way out* and on nothing else — one
   * button in a bar may take it, and three down a list may not (`ui/kit.css`).
   */
  private controls(view: ErasView): readonly HTMLElement[] {
    const turn = view.onTurn;

    if (turn === undefined || turn === null) {
      return [];
    }

    const controls: HTMLElement[] = [
      turner(
        "the age before",
        () => {
          turn(this.shown - 1);
        },
        this.shown === 0,
      ),
      turner(
        "the age after",
        () => {
          turn(this.shown + 1);
        },
        this.shown >= view.pages.length - 1,
      ),
    ];

    if (view.onBegin !== undefined && view.onBegin !== null) {
      const begin = view.onBegin;
      const out = turner("close the book", () => {
        begin();
      });

      out.classList.add("panel-button--lead");
      controls.push(out);
    }

    return controls;
  }
}

/** One page turner: {@link button} wearing the kit's shape. See `ErasPanel`'s `controls`. */
function turner(label: string, onPress: () => void, disabled = false): HTMLButtonElement {
  const element = button(label, onPress, disabled);

  element.classList.add("panel-button");

  return element;
}
